import React, { useEffect, useMemo, useState } from 'react';
import CustomSelect from '../components/CustomSelect';

const SCHOOL_TYPE_OPTIONS = [
  { value: 'school', label: 'School' },
  { value: 'camp', label: 'Camp' }
];

const SUBSCRIPTION_STATUS_OPTIONS = [
  { value: 'trial', label: 'Trial' },
  { value: 'active', label: 'Active' },
  { value: 'past_due', label: 'Past Due' },
  { value: 'cancelled', label: 'Cancelled' }
];

const PLATFORM_ROLE_OPTIONS = [
  { value: 'user', label: 'User' },
  { value: 'platform_admin', label: 'Platform Admin' }
];

const DANGER_ACCENT_STYLE = {
  borderLeft: '4px solid var(--danger, #e53e3e)',
  background: 'linear-gradient(135deg, rgba(229, 62, 62, 0.04) 0%, transparent 60%)'
};

const DANGER_KICKER_STYLE = {
  color: 'var(--danger, #e53e3e)',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  fontSize: '0.7rem'
};

export default function AdminView({
  adminWorkspace,
  isLoading,
  isSaving,
  saveError,
  onCreateSchool,
  onUpdateSchool,
  onDeleteSchool,
  onReseedSchool,
  onUpdateUserRole,
  onResetPassword,
  onAssignToSchool,
  onRemoveFromSchool,
  onMoveToSchool,
  onCreateUser,
  onUpdateSchoolUserRole
}) {
  const [activeTab, setActiveTab] = useState('schools');
  const [selectedSchoolId, setSelectedSchoolId] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [schoolDraft, setSchoolDraft] = useState(null);
  const [, setUserDraft] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [createDraft, setCreateDraft] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [userSchoolFilter, setUserSchoolFilter] = useState('all');
  const [tempPasswordDraft, setTempPasswordDraft] = useState('');
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);
  const [isSchoolMoveDialogOpen, setIsSchoolMoveDialogOpen] = useState(false);
  const [pendingSchoolMove, setPendingSchoolMove] = useState(null);
  const [isCreateUserDialogOpen, setIsCreateUserDialogOpen] = useState(false);
  const [createUserDraft, setCreateUserDraft] = useState({ email: '', firstName: '', lastName: '', tempPassword: '', schoolId: '', role: 'user' });
  const [isUserEditorOpen, setIsUserEditorOpen] = useState(false);
  const [userEditorDraft, setUserEditorDraft] = useState({ platformRole: 'user', schoolRole: 'user', schoolId: '' });
  const [schoolRoleSaveSuccess, setSchoolRoleSaveSuccess] = useState(false);
  const [isSchoolEditorOpen, setIsSchoolEditorOpen] = useState(false);
  const [reseedSuccess, setReseedSuccess] = useState(false);

  const schools = useMemo(() => {
    if (!adminWorkspace) return [];
    return adminWorkspace.schools;
  }, [adminWorkspace]);

  const users = useMemo(() => {
    if (!adminWorkspace) return [];
    return adminWorkspace.users;
  }, [adminWorkspace]);

  const filteredUsers = useMemo(() => {
    if (userSchoolFilter === 'all') return users;
    return users.filter((u) =>
      (u.schools ?? []).some((s) => s.schoolId === userSchoolFilter)
    );
  }, [users, userSchoolFilter]);

  const schoolFilterOptions = useMemo(() => {
    const opts = [{ value: 'all', label: 'All organizations' }];
    for (const s of schools) {
      opts.push({ value: s.id, label: s.name });
    }
    return opts;
  }, [schools]);

  // Sync selected school
  useEffect(() => {
    if (!adminWorkspace) {
      setSelectedSchoolId('');
      setSchoolDraft(null);
      setIsCreateDialogOpen(false);
      setCreateDraft(null);
      return;
    }

    const current = adminWorkspace.schools.find((s) => s.id === selectedSchoolId);
    const next = current ?? adminWorkspace.schools[0] ?? null;
    setSelectedSchoolId(next?.id ?? '');
  }, [adminWorkspace, selectedSchoolId]);

  // Build school draft when selection changes
  useEffect(() => {
    if (!adminWorkspace) {
      setSchoolDraft(null);
      return;
    }

    const selected = adminWorkspace.schools.find((s) => s.id === selectedSchoolId);
    setSchoolDraft(selected ? buildSchoolDraft(selected) : null);
  }, [adminWorkspace, selectedSchoolId]);

  // Sync selected user
  useEffect(() => {
    if (!adminWorkspace) {
      setSelectedUserId('');
      setUserDraft(null);
      return;
    }

    const current = adminWorkspace.users.find((u) => u.id === selectedUserId);
    const next = current ?? adminWorkspace.users[0] ?? null;
    setSelectedUserId(next?.id ?? '');
  }, [adminWorkspace, selectedUserId]);

  // Build user draft when selection changes
  useEffect(() => {
    if (!adminWorkspace) {
      setUserDraft(null);
      return;
    }

    const selected = adminWorkspace.users.find((u) => u.id === selectedUserId);
    setUserDraft(selected ? { platformRole: selected.platformRole } : null);
    if (selected) {
      const membership = (selected.schools ?? [])[0] ?? null;
      setUserEditorDraft({
        platformRole: selected.platformRole,
        schoolRole: membership?.role ?? 'user',
        schoolId: membership?.schoolId ?? ''
      });
    }
    setTempPasswordDraft('');
    setPasswordResetSuccess(false);
  }, [adminWorkspace, selectedUserId]);

  // Create dialog draft
  useEffect(() => {
    if (!adminWorkspace) {
      setCreateDraft(null);
      return;
    }

    if (isCreateDialogOpen) {
      setCreateDraft(createEmptySchoolDraft());
    }
  }, [adminWorkspace, isCreateDialogOpen]);

  // Escape key for create dialog
  useEffect(() => {
    if (!isCreateDialogOpen) return undefined;

    function handleKeyDown(event) {
      if (event.key === 'Escape' && !isSaving) {
        setIsCreateDialogOpen(false);
        setCreateDraft(null);
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isCreateDialogOpen, isSaving]);

  // Escape key for delete dialog
  useEffect(() => {
    if (!isDeleteDialogOpen) return undefined;

    function handleKeyDown(event) {
      if (event.key === 'Escape' && !isSaving) {
        setIsDeleteDialogOpen(false);
        setDeleteError('');
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDeleteDialogOpen, isSaving]);

  // Escape key for create user dialog
  useEffect(() => {
    if (!isCreateUserDialogOpen) return undefined;

    function handleKeyDown(event) {
      if (event.key === 'Escape' && !isSaving) {
        setIsCreateUserDialogOpen(false);
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isCreateUserDialogOpen, isSaving]);

  // Escape key for user editor dialog
  useEffect(() => {
    if (!isUserEditorOpen) return undefined;

    function handleKeyDown(event) {
      if (event.key === 'Escape' && !isSaving) {
        setIsUserEditorOpen(false);
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isUserEditorOpen, isSaving]);

  // Escape key for school editor dialog
  useEffect(() => {
    if (!isSchoolEditorOpen) return undefined;

    function handleKeyDown(event) {
      if (event.key === 'Escape' && !isSaving) {
        setIsSchoolEditorOpen(false);
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSchoolEditorOpen, isSaving]);

  if (isLoading) {
    return (
      <section className="panel roster-loading">
        <div className="loading-card">
          <div className="spinner-ring" />
          <h1>Loading platform data</h1>
          <p>Preparing the administration workspace.</p>
        </div>
      </section>
    );
  }

  if (!adminWorkspace) {
    return (
      <section className="panel roster-loading">
        <div className="loading-card">
          <h1>Unable to load platform data</h1>
          <p>You may not have permission to access platform administration.</p>
        </div>
      </section>
    );
  }

  // --- Schools tab handlers ---

  const selectedSchool = adminWorkspace.schools.find((s) => s.id === selectedSchoolId) ?? null;

  function handleSchoolDraftChange(field, value) {
    setSchoolDraft((d) => ({ ...d, [field]: value }));
  }

  function handleCreateDraftChange(field, value) {
    setCreateDraft((d) => ({ ...d, [field]: value }));
  }

  function handleSchoolReset() {
    if (!selectedSchool) return;
    setSchoolDraft(buildSchoolDraft(selectedSchool));
  }

  async function handleSchoolSave(event) {
    event.preventDefault();
    if (!schoolDraft || !selectedSchool) return;

    await onUpdateSchool(selectedSchool.id, {
      name: schoolDraft.name,
      slug: schoolDraft.slug,
      schoolType: schoolDraft.schoolType,
      subscriptionStatus: schoolDraft.subscriptionStatus,
      primaryContactName: schoolDraft.primaryContactName,
      primaryContactEmail: schoolDraft.primaryContactEmail,
      primaryContactPhone: schoolDraft.primaryContactPhone,
      timezone: schoolDraft.timezone
    });
  }

  function handleCreateClick() {
    if (isSaving) return;
    setIsCreateDialogOpen(true);
  }

  function handleCloseCreateDialog() {
    if (isSaving) return;
    setIsCreateDialogOpen(false);
    setCreateDraft(null);
  }

  async function handleCreateSubmit(event) {
    event.preventDefault();
    if (!createDraft) return;

    const payload = {
      name: createDraft.name,
      slug: createDraft.slug,
      schoolType: createDraft.schoolType,
      subscriptionStatus: createDraft.subscriptionStatus,
      primaryContactName: createDraft.primaryContactName,
      primaryContactEmail: createDraft.primaryContactEmail,
      primaryContactPhone: createDraft.primaryContactPhone,
      timezone: createDraft.timezone,
      seedSampleData: createDraft.seedSampleData
    };

    const updated = await onCreateSchool(payload);
    if (!updated) return;

    const created = updated.schools.find((s) => s.slug === payload.slug);
    setIsCreateDialogOpen(false);
    setCreateDraft(null);
    setSelectedSchoolId(created?.id ?? '');
  }

  function handleOpenDeleteDialog() {
    if (!selectedSchool || isSaving) return;
    setDeleteError('');
    setIsDeleteDialogOpen(true);
  }

  function handleCloseDeleteDialog() {
    if (isSaving) return;
    setIsDeleteDialogOpen(false);
    setDeleteError('');
  }

  async function handleConfirmDelete() {
    if (!selectedSchool || isSaving) return;
    setDeleteError('');

    try {
      const updated = await onDeleteSchool(selectedSchool.id);
      if (updated) {
        setIsDeleteDialogOpen(false);
        setIsSchoolEditorOpen(false);
        setSelectedSchoolId('');
      }
    } catch (err) {
      setDeleteError(err.message || 'Unable to delete this organization.');
    }
  }

  function handleOpenSchoolEditor(schoolId) {
    setSelectedSchoolId(schoolId);
    setIsSchoolEditorOpen(true);
    setReseedSuccess(false);
  }

  function handleCloseSchoolEditor() {
    if (isSaving) return;
    setIsSchoolEditorOpen(false);
  }

  async function handleReseedClick() {
    if (!selectedSchool || isSaving) return;
    setReseedSuccess(false);
    const result = await onReseedSchool(selectedSchool.id);
    if (result) {
      setReseedSuccess(true);
      setTimeout(() => setReseedSuccess(false), 3000);
    }
  }

  // --- Users tab handlers ---

  const selectedUser = adminWorkspace.users.find((u) => u.id === selectedUserId) ?? null;

  async function handleResetPassword(event) {
    event.preventDefault();
    if (!selectedUser || !tempPasswordDraft) return;
    const result = await onResetPassword(selectedUser.id, tempPasswordDraft);
    if (result) {
      setPasswordResetSuccess(true);
      setTimeout(() => setPasswordResetSuccess(false), 3000);
    }
  }

  function handleSchoolDropdownChange(newSchoolId) {
    if (!selectedUser) return;
    const currentMembership = (selectedUser.schools ?? [])[0] ?? null;
    const oldSchoolId = currentMembership?.schoolId ?? '';
    const oldSchoolName = currentMembership?.schoolName ?? '';

    if (newSchoolId === oldSchoolId) return;

    if (newSchoolId === '' && oldSchoolId) {
      // Removing from school
      onRemoveFromSchool(selectedUser.id, oldSchoolId);
      return;
    }

    const newSchool = schools.find((s) => s.id === newSchoolId);
    const newSchoolName = newSchool?.name ?? '';

    if (oldSchoolId) {
      // Moving between schools — confirm first
      setPendingSchoolMove({
        userId: selectedUser.id,
        oldSchoolId,
        oldSchoolName,
        newSchoolId,
        newSchoolName
      });
      setIsUserEditorOpen(false);
      setIsSchoolMoveDialogOpen(true);
    } else {
      // No current school — assign directly
      onAssignToSchool(selectedUser.id, { schoolId: newSchoolId, role: 'user' });
    }
  }

  function handleConfirmSchoolMove() {
    if (!pendingSchoolMove) return;
    onMoveToSchool(pendingSchoolMove.userId, {
      oldSchoolId: pendingSchoolMove.oldSchoolId,
      newSchoolId: pendingSchoolMove.newSchoolId,
      role: 'user'
    });
    setIsSchoolMoveDialogOpen(false);
    setPendingSchoolMove(null);
  }

  function handleCancelSchoolMove() {
    setIsSchoolMoveDialogOpen(false);
    setPendingSchoolMove(null);
  }

  // --- Create user handlers ---

  function handleCreateUserClick() {
    if (isSaving) return;
    setCreateUserDraft({ email: '', firstName: '', lastName: '', tempPassword: '', schoolId: '', role: 'user' });
    setIsCreateUserDialogOpen(true);
  }

  function handleCloseCreateUserDialog() {
    if (isSaving) return;
    setIsCreateUserDialogOpen(false);
  }

  async function handleCreateUserSubmit(event) {
    event.preventDefault();
    if (!createUserDraft) return;

    const result = await onCreateUser(createUserDraft);
    if (result) {
      setIsCreateUserDialogOpen(false);
    }
  }

  // --- User editor dialog handlers ---

  function handleOpenUserEditor(userId) {
    setSelectedUserId(userId);
    setIsUserEditorOpen(true);
    setSchoolRoleSaveSuccess(false);
  }

  function handleCloseUserEditor() {
    if (isSaving) return;
    setIsUserEditorOpen(false);
  }

  async function handleUserEditorSaveRole(event) {
    event.preventDefault();
    if (!selectedUser) return;
    const result = await onUpdateUserRole(selectedUser.id, userEditorDraft.platformRole);
    const membership = (selectedUser.schools ?? [])[0];
    if (membership && onUpdateSchoolUserRole && userEditorDraft.schoolRole !== membership.role) {
      await onUpdateSchoolUserRole(selectedUser.id, membership.schoolId, userEditorDraft.schoolRole);
    }
    if (result) {
      setSchoolRoleSaveSuccess(true);
      setTimeout(() => setSchoolRoleSaveSuccess(false), 1500);
    }
  }

  // --- Formatting helpers ---

  function formatDate(dateStr) {
    if (!dateStr) return 'Never';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return 'Unknown';
    }
  }

  function renderStatusBadge(status) {
    const colorMap = {
      active: 'accent',
      trial: 'purple',
      past_due: 'orange',
      cancelled: 'gray'
    };
    const labelMap = {
      active: 'Active',
      trial: 'Trial',
      past_due: 'Past Due',
      cancelled: 'Cancelled'
    };
    return (
      <span className={`tone-pill ${colorMap[status] ?? 'gray'}`}>
        {labelMap[status] ?? status}
      </span>
    );
  }

  function renderTypeBadge(type) {
    return (
      <span className={`tone-pill ${type === 'camp' ? 'purple' : 'accent'}`}>
        {type === 'camp' ? 'Camp' : 'School'}
      </span>
    );
  }

  function renderSchoolFormFields(draft, onChange, autoFocusFirst) {
    return (
      <div className="users-form-grid">
        <label className="auth-field">
          <span>Name</span>
          <input
            type="text"
            value={draft.name}
            onChange={(e) => onChange('name', e.target.value)}
            placeholder=""
            autoFocus={autoFocusFirst}
          />
        </label>

        <label className="auth-field">
          <span>Slug</span>
          <input
            type="text"
            value={draft.slug}
            onChange={(e) => onChange('slug', e.target.value)}
            placeholder=""
          />
        </label>

        <label className="auth-field">
          <span>Type</span>
          <CustomSelect
            value={draft.schoolType}
            options={SCHOOL_TYPE_OPTIONS}
            onChange={(val) => onChange('schoolType', val)}
          />
        </label>

        <label className="auth-field">
          <span>Subscription Status</span>
          <CustomSelect
            value={draft.subscriptionStatus}
            options={SUBSCRIPTION_STATUS_OPTIONS}
            onChange={(val) => onChange('subscriptionStatus', val)}
          />
        </label>

        <label className="auth-field">
          <span>Contact Name</span>
          <input
            type="text"
            value={draft.primaryContactName}
            onChange={(e) => onChange('primaryContactName', e.target.value)}
            placeholder=""
          />
        </label>

        <label className="auth-field">
          <span>Contact Email</span>
          <input
            type="email"
            value={draft.primaryContactEmail}
            onChange={(e) => onChange('primaryContactEmail', e.target.value)}
            placeholder=""
          />
        </label>

        <label className="auth-field">
          <span>Contact Phone</span>
          <input
            type="text"
            value={draft.primaryContactPhone}
            onChange={(e) => onChange('primaryContactPhone', e.target.value)}
            placeholder=""
          />
        </label>

        <label className="auth-field">
          <span>Timezone</span>
          <input
            type="text"
            value={draft.timezone}
            onChange={(e) => onChange('timezone', e.target.value)}
            placeholder=""
          />
        </label>

        {draft.seedSampleData !== undefined ? (
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={draft.seedSampleData}
              onChange={(e) => onChange('seedSampleData', e.target.checked)}
            />
            <span style={{ fontSize: '0.875rem' }}>Seed with sample data (demo project with 60 students)</span>
          </label>
        ) : null}
      </div>
    );
  }

  // --- Tab rendering ---

  function renderTabButtons() {
    return (
      <div className="admin-tab-bar" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <button
          type="button"
          className={`utility-button ${activeTab === 'schools' ? 'admin-tab-active' : ''}`}
          onClick={() => setActiveTab('schools')}
          style={activeTab === 'schools' ? { borderColor: 'var(--danger, #e53e3e)', color: 'var(--danger, #e53e3e)' } : {}}
        >
          Organizations
        </button>
        <button
          type="button"
          className={`utility-button ${activeTab === 'users' ? 'admin-tab-active' : ''}`}
          onClick={() => setActiveTab('users')}
          style={activeTab === 'users' ? { borderColor: 'var(--danger, #e53e3e)', color: 'var(--danger, #e53e3e)' } : {}}
        >
          Users
        </button>
      </div>
    );
  }

  function renderSchoolsTab() {
    return (
      <section>
        <div className="panel">
          <div className="panel-head">
            <div>
              <span className="panel-kicker">Organization directory</span>
              <h2>All organizations</h2>
            </div>
            <span className="toolbar-meta">{schools.length} organization{schools.length === 1 ? '' : 's'}</span>
          </div>

          <div className="table-wrap roster-list-wrap">
            <table className="data-grid">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Slug</th>
                  <th>Status</th>
                  <th>Users</th>
                  <th>Projects</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {schools.length === 0 ? (
                  <tr>
                    <td colSpan={7}>No organizations found</td>
                  </tr>
                ) : null}
                {schools.map((school) => (
                  <tr
                    key={school.id}
                    className={school.id === selectedSchoolId && isSchoolEditorOpen ? 'is-selected' : ''}
                    onClick={() => handleOpenSchoolEditor(school.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <div className="list-cell">
                        <strong>{school.name}</strong>
                        <span>{school.primaryContactName}</span>
                      </div>
                    </td>
                    <td>{renderTypeBadge(school.schoolType)}</td>
                    <td>{school.slug}</td>
                    <td>{renderStatusBadge(school.subscriptionStatus)}</td>
                    <td>{school.userCount}</td>
                    <td>{school.projectCount}</td>
                    <td>{formatDate(school.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    );
  }

  function renderUsersTab() {
    return (
      <section>
        <div className="panel">
          <div className="panel-head">
            <div>
              <span className="panel-kicker">Platform users</span>
              <h2>All users</h2>
            </div>
            <span className="toolbar-meta">{filteredUsers.length} user{filteredUsers.length === 1 ? '' : 's'}</span>
          </div>

          <div className="roster-filter-bar" style={{ padding: '0 1rem 0.75rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
              <span style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem', color: '#64748b' }}>Organization</span>
              <CustomSelect
                value={userSchoolFilter}
                options={schoolFilterOptions}
                onChange={(val) => setUserSchoolFilter(val)}
              />
            </label>
          </div>

          <div className="table-wrap roster-list-wrap">
            <table className="data-grid">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Platform Role</th>
                  <th>Organizations</th>
                  <th>Active</th>
                  <th>Last Login</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6}>No users found</td>
                  </tr>
                ) : null}
                {filteredUsers.map((user) => (
                  <tr
                    key={user.id}
                    className={user.id === selectedUserId && isUserEditorOpen ? 'is-selected' : ''}
                    onClick={() => handleOpenUserEditor(user.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <div className="list-cell">
                        <strong>{user.displayName}</strong>
                        <span>{user.firstName} {user.lastName}</span>
                      </div>
                    </td>
                    <td>{user.email}</td>
                    <td>
                      {user.platformRole === 'platform_admin' ? (
                        <span className="tone-pill" style={{ background: 'var(--danger, #e53e3e)', color: '#fff' }}>
                          Super Admin
                        </span>
                      ) : (
                        <span className="tone-pill gray">User</span>
                      )}
                    </td>
                    <td>
                      {(user.schools ?? []).map((s) => s.schoolName).join(', ') || 'None'}
                    </td>
                    <td>{user.isActive ? 'Active' : 'Inactive'}</td>
                    <td>{formatDate(user.lastLoginAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    );
  }

  return (
    <div className="admin-screen">
      <section className="hero-card roster-hero" style={DANGER_ACCENT_STYLE}>
        <div className="hero-copy">
          <div className="eyebrow-row">
            <span className="tone-pill" style={{ background: 'var(--danger, #e53e3e)', color: '#fff' }}>
              SUPER ADMIN
            </span>
            <span className="eyebrow-copy">
              {schools.length} organization{schools.length === 1 ? '' : 's'} &middot; {users.length} user{users.length === 1 ? '' : 's'}
            </span>
          </div>
          <h1 style={{ color: 'var(--danger, #e53e3e)' }}>Platform Administration</h1>
          <p>
            Manage all organizations and platform-wide user roles across the entire SmartSort system.
          </p>
        </div>

        <div className="hero-actions">
          <div className="roster-stat-block">
            <span className="metric-label">Organizations</span>
            <strong>{schools.length}</strong>
          </div>
          <div className="roster-stat-block">
            <span className="metric-label">Users</span>
            <strong>{users.length}</strong>
          </div>
          <div className="roster-stat-block">
            <span className="metric-label">Admins</span>
            <strong>{users.filter((u) => u.platformRole === 'platform_admin').length}</strong>
          </div>
        </div>
        <div className="roster-action-bar">
          {activeTab === 'schools' ? (
            <button
              type="button"
              className="primary-button compact-button"
              onClick={handleCreateClick}
              disabled={isSaving}
            >
              + Create organization
            </button>
          ) : (
            <button
              type="button"
              className="primary-button compact-button"
              onClick={handleCreateUserClick}
              disabled={isSaving}
            >
              + Create user
            </button>
          )}
        </div>
      </section>

      <div style={{ height: '1rem' }} />

      {renderTabButtons()}

      {activeTab === 'schools' ? renderSchoolsTab() : renderUsersTab()}

      {/* Create organization dialog */}
      {isCreateDialogOpen ? (
        <div className="dialog-overlay" onClick={handleCloseCreateDialog}>
          <div
            className="dialog-panel panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-school-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="panel-head compact">
              <div>
                <span className="panel-kicker" style={DANGER_KICKER_STYLE}>New organization</span>
                <h2 id="create-school-title">Create organization</h2>
              </div>
              <button
                type="button"
                className="icon-button classes-dialog-close"
                onClick={handleCloseCreateDialog}
                aria-label="Close create organization dialog"
                disabled={isSaving}
              >
                &times;
              </button>
            </div>

            {saveError ? <div className="auth-error">{saveError}</div> : null}

            <form className="roster-form" onSubmit={handleCreateSubmit}>
              {createDraft ? renderSchoolFormFields(createDraft, handleCreateDraftChange, true) : null}

              <div className="roster-form-actions">
                <button
                  type="button"
                  className="utility-button"
                  onClick={handleCloseCreateDialog}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button type="submit" className="primary-button" disabled={isSaving}>
                  {isSaving ? <span className="button-spinner" aria-hidden="true" /> : null}
                  {isSaving ? 'Creating organization' : 'Create organization'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {/* Delete organization dialog */}
      {isDeleteDialogOpen && selectedSchool ? (
        <div className="dialog-overlay" onClick={handleCloseDeleteDialog}>
          <div
            className="dialog-panel panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-school-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="panel-head compact">
              <div>
                <span className="panel-kicker">Confirm deletion</span>
                <h2 id="delete-school-title">Delete {selectedSchool.name}?</h2>
              </div>
              <button
                type="button"
                className="icon-button classes-dialog-close"
                onClick={handleCloseDeleteDialog}
                aria-label="Close delete confirmation"
                disabled={isSaving}
              >
                &times;
              </button>
            </div>

            {deleteError ? <div className="auth-error">{deleteError}</div> : null}

            <div className="delete-confirm-body">
              <p>
                This will permanently remove <strong>{selectedSchool.name}</strong> ({selectedSchool.slug}) and all its associated data, including {selectedSchool.userCount} user{selectedSchool.userCount === 1 ? '' : 's'} and {selectedSchool.projectCount} project{selectedSchool.projectCount === 1 ? '' : 's'}.
              </p>

              <div className="roster-form-actions">
                <button
                  type="button"
                  className="utility-button"
                  onClick={handleCloseDeleteDialog}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="primary-button danger-button"
                  onClick={handleConfirmDelete}
                  disabled={isSaving}
                >
                  {isSaving ? <span className="button-spinner" aria-hidden="true" /> : null}
                  {isSaving ? 'Deleting' : 'Delete organization'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* School editor dialog */}
      {isSchoolEditorOpen && selectedSchool && schoolDraft ? (
        <div className="dialog-overlay" onClick={handleCloseSchoolEditor}>
          <div
            className="dialog-panel panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="school-editor-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="panel-head compact">
              <div>
                <span className="panel-kicker" style={DANGER_KICKER_STYLE}>Organization editor</span>
                <h2 id="school-editor-title">{selectedSchool.name}</h2>
              </div>
              <button
                type="button"
                className="icon-button classes-dialog-close"
                onClick={handleCloseSchoolEditor}
                aria-label="Close organization editor"
                disabled={isSaving}
              >
                &times;
              </button>
            </div>

            {saveError ? <div className="auth-error">{saveError}</div> : null}

            <div className="admin-user-editor-body">
              <div className="admin-editor-card">
                <form className="roster-form" onSubmit={handleSchoolSave}>
                  {renderSchoolFormFields(schoolDraft, handleSchoolDraftChange, false)}

                  <div className="roster-form-actions">
                    <button
                      type="button"
                      className="utility-button danger-text"
                      onClick={handleOpenDeleteDialog}
                      disabled={isSaving}
                    >
                      Delete
                    </button>
                    <button
                      type="button"
                      className="utility-button"
                      onClick={handleSchoolReset}
                      disabled={isSaving}
                    >
                      Reset
                    </button>
                    <button type="submit" className="primary-button" disabled={isSaving}>
                      {isSaving ? <span className="button-spinner" aria-hidden="true" /> : null}
                      {isSaving ? 'Saving organization' : 'Save organization'}
                    </button>
                  </div>
                </form>
              </div>

              <div className="admin-editor-card">
                <span className="panel-kicker" style={DANGER_KICKER_STYLE}>Demo Data</span>
                <p style={{ fontSize: '0.85rem', margin: '0.5rem 0', color: 'var(--muted)' }}>
                  Delete all existing demo project data and re-generate fresh students, classes, and relationships.
                </p>
                {reseedSuccess ? (
                  <p className="admin-save-flash">Demo data reseeded successfully.</p>
                ) : null}
                <button
                  type="button"
                  className="utility-button"
                  onClick={handleReseedClick}
                  disabled={isSaving}
                >
                  {isSaving ? <span className="button-spinner" aria-hidden="true" /> : null}
                  {isSaving ? 'Reseeding...' : 'Reseed Demo Data'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {isSchoolMoveDialogOpen && pendingSchoolMove ? (
        <div className="dialog-overlay" onClick={handleCancelSchoolMove}>
          <div
            className="dialog-panel panel"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '480px' }}
          >
            <div className="panel-head compact">
              <div>
                <span className="panel-kicker">Confirm organization change</span>
                <h2>Move user?</h2>
              </div>
              <button
                type="button"
                className="icon-button classes-dialog-close"
                onClick={handleCancelSchoolMove}
                aria-label="Close confirmation"
                disabled={isSaving}
              >
                &times;
              </button>
            </div>

            <div className="delete-confirm-body">
              <p>
                This will move <strong>{selectedUser?.displayName}</strong> from <strong>{pendingSchoolMove.oldSchoolName}</strong> to <strong>{pendingSchoolMove.newSchoolName}</strong>. Continue?
              </p>

              <div className="roster-form-actions">
                <button
                  type="button"
                  className="utility-button"
                  onClick={handleCancelSchoolMove}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="primary-button"
                  onClick={handleConfirmSchoolMove}
                  disabled={isSaving}
                >
                  {isSaving ? <span className="button-spinner" aria-hidden="true" /> : null}
                  {isSaving ? 'Moving' : 'Move user'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Create user dialog */}
      {isCreateUserDialogOpen ? (
        <div className="dialog-overlay" onClick={handleCloseCreateUserDialog}>
          <div
            className="dialog-panel panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-user-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="panel-head compact">
              <div>
                <span className="panel-kicker" style={DANGER_KICKER_STYLE}>New user</span>
                <h2 id="create-user-title">Create user</h2>
              </div>
              <button
                type="button"
                className="icon-button classes-dialog-close"
                onClick={handleCloseCreateUserDialog}
                aria-label="Close create user dialog"
                disabled={isSaving}
              >
                &times;
              </button>
            </div>

            {saveError ? <div className="auth-error">{saveError}</div> : null}

            <form className="roster-form" onSubmit={handleCreateUserSubmit}>
              <div className="users-form-grid">
                <label className="auth-field">
                  <span>First Name</span>
                  <input
                    type="text"
                    value={createUserDraft.firstName}
                    onChange={(e) => setCreateUserDraft((d) => ({ ...d, firstName: e.target.value }))}
                    placeholder=""
                    autoFocus
                  />
                </label>

                <label className="auth-field">
                  <span>Last Name</span>
                  <input
                    type="text"
                    value={createUserDraft.lastName}
                    onChange={(e) => setCreateUserDraft((d) => ({ ...d, lastName: e.target.value }))}
                    placeholder=""
                  />
                </label>

                <label className="auth-field">
                  <span>Email</span>
                  <input
                    type="email"
                    value={createUserDraft.email}
                    onChange={(e) => setCreateUserDraft((d) => ({ ...d, email: e.target.value }))}
                    placeholder=""
                  />
                </label>

                <label className="auth-field">
                  <span>Temporary Password</span>
                  <input
                    type="password"
                    value={createUserDraft.tempPassword}
                    onChange={(e) => setCreateUserDraft((d) => ({ ...d, tempPassword: e.target.value }))}
                    placeholder=""
                  />
                </label>

                <label className="auth-field">
                  <span>Organization</span>
                  <CustomSelect
                    value={createUserDraft.schoolId}
                    options={[
                      { value: '', label: '\u2014 None \u2014' },
                      ...schools.map((s) => ({ value: s.id, label: s.name }))
                    ]}
                    onChange={(val) => setCreateUserDraft((d) => ({ ...d, schoolId: val }))}
                  />
                </label>

                <label className="auth-field">
                  <span>Role</span>
                  <CustomSelect
                    value={createUserDraft.role}
                    options={[
                      { value: 'user', label: 'User' },
                      { value: 'admin', label: 'Admin' }
                    ]}
                    onChange={(val) => setCreateUserDraft((d) => ({ ...d, role: val }))}
                  />
                </label>
              </div>

              <div className="roster-form-actions">
                <button
                  type="button"
                  className="utility-button"
                  onClick={handleCloseCreateUserDialog}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button type="submit" className="primary-button" disabled={isSaving}>
                  {isSaving ? <span className="button-spinner" aria-hidden="true" /> : null}
                  {isSaving ? 'Creating user' : 'Create user'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {/* User editor dialog */}
      {isUserEditorOpen && selectedUser ? (
        <div className="dialog-overlay" onClick={handleCloseUserEditor}>
          <div
            className="dialog-panel panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="user-editor-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="panel-head compact">
              <div>
                <span className="panel-kicker">User details</span>
                <h2 id="user-editor-title">{selectedUser.displayName}</h2>
                <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>{selectedUser.email}</p>
              </div>
              <button
                type="button"
                className="icon-button classes-dialog-close"
                onClick={handleCloseUserEditor}
                aria-label="Close user editor"
                disabled={isSaving}
              >
                &times;
              </button>
            </div>

            {saveError ? <div className="auth-error">{saveError}</div> : null}

            <div className="admin-user-editor-body">
              <div className="admin-editor-card">
                <form onSubmit={handleUserEditorSaveRole}>
                  <div className="users-form-grid">
                    <label className="auth-field">
                      <span>Platform Role</span>
                      <CustomSelect
                        value={userEditorDraft.platformRole}
                        options={PLATFORM_ROLE_OPTIONS}
                        onChange={(val) => setUserEditorDraft((d) => ({ ...d, platformRole: val }))}
                      />
                    </label>
                    <label className="auth-field">
                      <span>Organization Assignment</span>
                      <CustomSelect
                        value={selectedUser.schools?.[0]?.schoolId ?? ''}
                        options={[
                          { value: '', label: '\u2014 None \u2014' },
                          ...schools.map((s) => ({ value: s.id, label: s.name }))
                        ]}
                        onChange={handleSchoolDropdownChange}
                      />
                    </label>
                    {(selectedUser.schools ?? []).length > 0 ? (
                      <label className="auth-field">
                        <span>Organization Role</span>
                        <CustomSelect
                          value={userEditorDraft.schoolRole}
                          options={[
                            { value: 'admin', label: 'Admin' },
                            { value: 'user', label: 'User' }
                          ]}
                          onChange={(val) => { setUserEditorDraft((d) => ({ ...d, schoolRole: val })); setSchoolRoleSaveSuccess(false); }}
                        />
                      </label>
                    ) : null}
                  </div>
                  <div className="roster-form-actions" style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {schoolRoleSaveSuccess ? (
                      <span className="admin-save-flash">Saved successfully</span>
                    ) : null}
                    <button type="submit" className="primary-button" disabled={isSaving}>
                      {isSaving ? <span className="button-spinner" aria-hidden="true" /> : null}
                      {isSaving ? 'Saving...' : 'Save changes'}
                    </button>
                  </div>
                </form>
              </div>

              <div className="admin-editor-card">
                <form onSubmit={handleResetPassword}>
                  <label className="auth-field">
                    <span>Password Reset</span>
                    <div className="admin-password-reset-row">
                      <input
                        type="password"
                        placeholder=""
                        value={tempPasswordDraft}
                        onChange={(e) => { setTempPasswordDraft(e.target.value); setPasswordResetSuccess(false); }}
                      />
                      <button
                        type="submit"
                        className="primary-button admin-reset-btn"
                        disabled={isSaving || !tempPasswordDraft || tempPasswordDraft.length < 8}
                      >
                        {isSaving ? <span className="button-spinner" aria-hidden="true" /> : null}
                        Reset
                      </button>
                    </div>
                  </label>
                  {passwordResetSuccess ? (
                    <p className="admin-password-success">Password reset. User will be prompted to change it on next login.</p>
                  ) : null}
                  {tempPasswordDraft && tempPasswordDraft.length < 8 && tempPasswordDraft.length > 0 ? (
                    <p className="admin-password-hint">Minimum 8 characters</p>
                  ) : null}
                </form>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function buildSchoolDraft(school) {
  return {
    name: school.name ?? '',
    slug: school.slug ?? '',
    schoolType: school.schoolType ?? 'school',
    subscriptionStatus: school.subscriptionStatus ?? 'trial',
    primaryContactName: school.primaryContactName ?? '',
    primaryContactEmail: school.primaryContactEmail ?? '',
    primaryContactPhone: school.primaryContactPhone ?? '',
    timezone: school.timezone ?? ''
  };
}

function createEmptySchoolDraft() {
  return {
    name: '',
    slug: '',
    schoolType: 'school',
    subscriptionStatus: 'trial',
    primaryContactName: '',
    primaryContactEmail: '',
    primaryContactPhone: '',
    timezone: 'America/New_York',
    seedSampleData: true
  };
}
